import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import BASE_URL from "../../BASEURL";

export default function HomeCMS() {
  const navigate = useNavigate();

  const initialData = {
    hero: [],
    about: {
      _id: null,
      title: "",
      description1: "",
      description2: "",
      description3: "",
      image: "",
    },
    features: {
      _id: null,
      list: [],
    },
    whyChoose: {
      _id: null,
      list: [],
    },
  };

  const [homeData, setHomeData] = useState(initialData);
  const [showPopup, setShowPopup] = useState(false);
  const [editSection, setEditSection] = useState("");

  const [formData, setFormData] = useState({
    title: "",
    description1: "",
    description2: "",
    description3: "",
    image: null,
    features: [],
    whyChoose: [],
  });

  const [aboutImagePreview, setAboutImagePreview] = useState("");

  // ================= FETCH DATA =================
  useEffect(() => {

    // HERO
    fetch(`${BASE_URL}/api/hero`)
      .then(res => res.json())
      .then(data => {
        const slides = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        const mapped = slides.map(s => ({
          title: s.title || "",
          subtitle: s.subtitle || "",
          description: s.description || "",
          image: s.productImage || s.image || "",
          bgImage: s.backgroundImage || s.background || s.bgImage || "",
        }));
        setHomeData(prev => ({ ...prev, hero: mapped }));
      });

    // ABOUT
    fetch(`${BASE_URL}/api/about`)
      .then(res => res.json())
      .then(data => {
        const aboutPayload = Array.isArray(data?.data) ? data.data[0] : data?.data || data;
        if (aboutPayload) {
          setHomeData(prev => ({
            ...prev,
            about: {
              _id: aboutPayload._id,
              title: aboutPayload.title || "",
              description1: aboutPayload.description1 || "",
              description2: aboutPayload.description2 || "",
              description3: aboutPayload.description3 || "",
              image: aboutPayload.image ? `${aboutPayload.image}` : "",
            },
          }));
        }
      });

    // FEATURES
    fetch(`${BASE_URL}/api/keyfeatures`)
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data?.data) && data.data.length > 0) {
          const item = data.data[0];
          setHomeData(prev => ({
            ...prev,
            features: {
              _id: item._id,
              list: item.features || [],
            },
          }));
        }
      });

    // WHY CHOOSE
    fetch(`${BASE_URL}/api/why-choose-us/all`)
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data?.data) && data.data.length > 0) {
          const item = data.data[0];
          setHomeData(prev => ({
            ...prev,
            whyChoose: {
              _id: item._id,
              list: item.features || [],
            },
          }));
        }
      });

  }, []);

  // ================= OPEN POPUP =================
  const openPopup = (section) => {
    setEditSection(section);

    if (section === "about") {
      setFormData({
        title: homeData.about.title,
        description1: homeData.about.description1,
        description2: homeData.about.description2,
        description3: homeData.about.description3,
        image: null,
      });
      setAboutImagePreview(homeData.about.image);
    }

    if (section === "features") {
      setFormData({ features: [...homeData.features.list] });
    }

    if (section === "whyChoose") {
      setFormData({ whyChoose: [...homeData.whyChoose.list] });
    }

    setShowPopup(true);
  };

  // ================= SAVE =================
  const handleSave = async () => {
    try {

      // -------- ABOUT --------
      if (editSection === "about") {
        const form = new FormData();
        form.append("title", formData.title);
        form.append("description1", formData.description1);
        form.append("description2", formData.description2);
        form.append("description3", formData.description3);
        if (formData.image) form.append("image", formData.image);

        const res = await fetch(`${BASE_URL}/api/about/${homeData.about._id}`, {
          method: "PUT",
          body: form,
        });

        const result = await res.json();

        if (result.success) {
          setHomeData(prev => ({
            ...prev,
            about: {
              _id: result.data._id,
              title: result.data.title,
              description1: result.data.description1,
              description2: result.data.description2,
              description3: result.data.description3,
              image: result.data.image
                ? `${BASE_URL}${result.data.image}`
                : prev.about.image,
            },
          }));
        }
      }

      // -------- FEATURES --------
      if (editSection === "features") {
        await fetch(`${BASE_URL}/api/keyfeatures/${homeData.features._id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            title: "Key Features",
            features: formData.features,
          }),
        });

        setHomeData(prev => ({
          ...prev,
          features: { ...prev.features, list: formData.features },
        }));
      }

      // -------- WHY CHOOSE --------
      if (editSection === "whyChoose") {
        await fetch(`${BASE_URL}/api/why-choose-us/${homeData.whyChoose._id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            title: "Why Choose Us",
            features: formData.whyChoose,
          }),
        });

        setHomeData(prev => ({
          ...prev,
          whyChoose: { ...prev.whyChoose, list: formData.whyChoose },
        }));
      }

      alert("Updated successfully ✅");
      setShowPopup(false);

    } catch (err) {
      console.error("Update failed:", err);
      alert("Update failed ❌");
    }
  };

  return (
    <div className="max-w-7xl mx-auto md:p-6 space-y-6">

      {/* HEADER */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-primaryDark">Home Page CMS</h1>
        <button
          onClick={() => navigate("/admin/homecms/edit")}
          className="bg-primary text-white px-6 py-2 rounded"
        >
          Edit Hero Only
        </button>
      </div>

      {/* HERO */}
      <section className="bg-white p-6 rounded-xl shadow">
        <h2 className="text-xl font-semibold mb-4">Hero Section</h2>
        {homeData.hero.map((slide, i) => (
          <div key={i} className="border-b pb-4 mb-4">
            <h3 className="font-semibold">{slide.title}</h3>
            <p>{slide.subtitle}</p>
            <p>{slide.description}</p>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <img src={slide.image} className="h-28 object-contain rounded" />
              <img src={slide.bgImage} className="h-28 object-cover rounded" />
            </div>
          </div>
        ))}
      </section>

      {/* ABOUT */}
      <section className="bg-white p-6 rounded-xl shadow">
        <div className="flex justify-between mb-3">
          <h2 className="text-xl font-semibold">About</h2>
          <button onClick={() => openPopup("about")} className="bg-primary text-white px-4 py-1 rounded">Edit</button>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-semibold">{homeData.about.title}</h3>
            <p>{homeData.about.description2}</p>
            <p>{homeData.about.description3}</p>
          </div>
          {homeData.about.image && (
            <img src={homeData.about.image} className="w-full h-60 object-cover rounded" />
          )}
        </div>
      </section>

      {/* FEATURES */}
      <section className="bg-white p-6 rounded-xl shadow">
        <div className="flex justify-between mb-3">
          <h2 className="text-xl font-semibold">Key Features</h2>
          <button onClick={() => openPopup("features")} className="bg-primary text-white px-4 py-1 rounded">Edit</button>
        </div>
        <ul className="list-disc ml-6">
          {homeData.features.list.map((f,i)=>(
            <li key={i}>{f}</li>
          ))}
        </ul>
      </section>

      {/* WHY CHOOSE */}
      <section className="bg-white p-6 rounded-xl shadow">
        <div className="flex justify-between mb-3">
          <h2 className="text-xl font-semibold">Why Choose Us</h2>
          <button onClick={() => openPopup("whyChoose")} className="bg-primary text-white px-4 py-1 rounded">Edit</button>
        </div>
        <ul className="grid md:grid-cols-2 gap-3">
          {homeData.whyChoose.list.map((f,i)=>(
            <li key={i} className="p-3 border rounded">{f}</li>
          ))}
        </ul>
      </section>

      {/* POPUP */}
      {showPopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-xl rounded-xl p-6">

            <h2 className="text-xl font-bold mb-4">Edit {editSection}</h2>

            {editSection === "about" && (
              <div className="space-y-3">
                <input className="w-full border p-2 rounded" value={formData.title}
                  onChange={e=>setFormData({...formData,title:e.target.value})}/>
               
                <textarea className="w-full border p-2 rounded" value={formData.description2}
                  onChange={e=>setFormData({...formData,description2:e.target.value})}/>
                <textarea className="w-full border p-2 rounded" value={formData.description3}
                  onChange={e=>setFormData({...formData,description3:e.target.value})}/>

                {aboutImagePreview && (
                  <img src={aboutImagePreview} className="h-40 w-full object-cover rounded"/>
                )}

                <input type="file" accept="image/*"
                  onChange={e=>{
                    const file=e.target.files[0];
                    if(file){
                      setFormData({...formData,image:file});
                      setAboutImagePreview(URL.createObjectURL(file));
                    }
                  }}/>
              </div>
            )}

            {editSection === "features" && (
              <div className="space-y-2">
                {formData.features.map((f,i)=>(
                  <input key={i} className="w-full border p-2 rounded" value={f}
                    onChange={e=>{
                      const arr=[...formData.features];
                      arr[i]=e.target.value;
                      setFormData({...formData,features:arr});
                    }}/>
                ))}
                <button onClick={()=>setFormData({...formData,features:[...formData.features,""]})}
                  className="text-primary font-semibold text-sm">+ Add</button>
              </div>
            )}

            {editSection === "whyChoose" && (
              <div className="space-y-2">
                {formData.whyChoose.map((f,i)=>(
                  <input key={i} className="w-full border p-2 rounded" value={f}
                    onChange={e=>{
                      const arr=[...formData.whyChoose];
                      arr[i]=e.target.value;
                      setFormData({...formData,whyChoose:arr});
                    }}/>
                ))}
                <button onClick={()=>setFormData({...formData,whyChoose:[...formData.whyChoose,""]})}
                  className="text-primary font-semibold text-sm">+ Add</button>
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={()=>setShowPopup(false)} className="border px-4 py-2 rounded">Cancel</button>
              <button onClick={handleSave} className="bg-primary text-white px-6 py-2 rounded">Save</button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}
